/**
 * Chart wrappers.
 *
 * Chart.js, as the prototype uses, but registered once here and wrapped so
 * screens pass data rather than options. Every chart in the app then shares one
 * set of axis, grid, tooltip and legend decisions — which is what makes a
 * dashboard read as one system instead of five charts that happen to be adjacent.
 *
 * The palette is the brand ramp from `src/styles/app.css`, read from CSS custom
 * properties so a token change moves the charts with everything else.
 */
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useMemo } from 'react';
import { moneyShort } from '../../utils/formatters.js';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement,
  Tooltip, Legend, Filler,
);

/**
 * Categorical series colours, in the order they should be used.
 *
 * Ordered for maximum separation between adjacent pairs, not by hue — the first
 * two are the ones most often compared, so they must be the least confusable.
 */
export const SERIES = ['#6366f1', '#14b8a6', '#f59e0b', '#ec4899', '#0ea5e9', '#8b5cf6', '#10b981', '#f43f5e'];

const INK_3 = '#7b829b';
const LINE = '#e7e9f3';

/**
 * Shared axis, grid and tooltip treatment.
 *
 * `horizontal` matters more than it looks. Chart.js draws a horizontal bar chart
 * by setting `indexAxis: 'y'`, which swaps the *roles* of the two axes: y
 * becomes the category axis and x becomes the value axis. Options written as
 * "x is categories, y is values" therefore end up backwards — the money
 * formatter lands on the category axis, where the tick value is a row index, and
 * renders "Analytics Platform" as `$0`. So the treatments are built once and
 * assigned to whichever axis is playing which part.
 */
function baseOptions({ money = false, stacked = false, legend = false, horizontal = false } = {}) {
  const categoryAxis = {
    stacked,
    grid: { display: false },
    border: { display: false },
    ticks: { color: INK_3, font: { size: 11 }, autoSkip: false },
  };

  const valueAxis = {
    stacked,
    beginAtZero: true,
    grid: { color: LINE, drawTicks: false },
    border: { display: false },
    ticks: {
      color: INK_3,
      font: { size: 11 },
      padding: 8,
      callback: money ? (v) => moneyShort(v) : undefined,
    },
  };

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: legend
        ? { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, font: { size: 11 }, color: INK_3 } }
        : { display: false },
      tooltip: {
        backgroundColor: '#14172b',
        padding: 10,
        cornerRadius: 8,
        titleFont: { size: 12, weight: '600' },
        bodyFont: { size: 12 },
        callbacks: money
          ? {
              /* Same swap: the value lives on x when the chart is turned on its side. */
              label: (ctx) => {
                const value = horizontal ? ctx.parsed.x : ctx.parsed.y;
                return `${ctx.dataset.label ?? ''}  ${moneyShort(value ?? ctx.parsed)}`.trim();
              },
            }
          : undefined,
      },
    },
    scales: horizontal
      ? { x: valueAxis, y: categoryAxis }
      : { x: categoryAxis, y: valueAxis },
  };
}

/**
 * A trend over time, optionally with a second comparison series.
 *
 * @param {{data:object[], xKey:string, series:{key:string,label:string}[],
 *          money?:boolean, height?:number, area?:boolean}} props
 */
export function LineChart({ data, xKey, series, money = false, height = 220, area = true }) {
  const chartData = useMemo(
    () => ({
      labels: data.map((d) => d[xKey]),
      datasets: series.map((s, i) => ({
        label: s.label,
        data: data.map((d) => d[s.key]),
        borderColor: SERIES[i % SERIES.length],
        backgroundColor: area ? `${SERIES[i % SERIES.length]}1f` : 'transparent',
        fill: area,
        tension: 0.35,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
      })),
    }),
    [data, xKey, series, area],
  );

  return (
    <div style={{ height }}>
      <Line data={chartData} options={baseOptions({ money, legend: series.length > 1 })} />
    </div>
  );
}

/**
 * Categorical comparison. Horizontal when the labels are long — a bar chart
 * whose x-axis labels are rotated 45° is harder to read than the same data
 * turned on its side.
 */
export function BarChart({ data, xKey, series, money = false, height = 220, horizontal = false, stacked = false }) {
  const chartData = useMemo(
    () => ({
      labels: data.map((d) => d[xKey]),
      datasets: series.map((s, i) => ({
        label: s.label,
        data: data.map((d) => d[s.key]),
        backgroundColor: s.color ?? SERIES[i % SERIES.length],
        borderRadius: 5,
        borderSkipped: false,
        barPercentage: 0.7,
        categoryPercentage: 0.72,
      })),
    }),
    [data, xKey, series],
  );

  const options = {
    ...baseOptions({ money, stacked, legend: series.length > 1, horizontal }),
    indexAxis: horizontal ? 'y' : 'x',
  };
  return (
    <div style={{ height }}>
      <Bar data={chartData} options={options} />
    </div>
  );
}

/**
 * Parts of a whole.
 *
 * A doughnut rather than a pie so the centre can carry the total — the number
 * people actually want, which a pie makes them compute from the segments.
 */
export function DonutChart({
  data, labelKey = 'label', valueKey = 'value', height = 200,
  centerLabel, centerValue, colors, legend = true,
}) {
  const chartData = useMemo(
    () => ({
      labels: data.map((d) => d[labelKey]),
      datasets: [
        {
          data: data.map((d) => d[valueKey]),
          /* `colors` is for the rings whose segments mean something ordered —
             healthy → critical, or closed → pipeline. Running those through the
             categorical palette, which is built for maximum separation between
             neighbours, throws away the ordering the reader is looking for. */
          backgroundColor: colors ?? data.map((_, i) => SERIES[i % SERIES.length]),
          borderWidth: 0,
          hoverOffset: 6,
        },
      ],
    }),
    [data, labelKey, valueKey, colors],
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: legend
        ? { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, font: { size: 11 }, color: INK_3 } }
        : { display: false },
      tooltip: { backgroundColor: '#14172b', padding: 10, cornerRadius: 8 },
    },
  };

  return (
    <div className="donut-wrap" style={{ height }}>
      <Doughnut data={chartData} options={options} />
      {centerValue != null && (
        <div className="donut-center">
          <div className="donut-center-value">{centerValue}</div>
          {centerLabel && <div className="donut-center-label">{centerLabel}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * Bars and lines on one set of axes.
 *
 * Two dashboards need this and neither `LineChart` nor `BarChart` can express
 * it: the manager's revenue card stacks closed / commit / best-case bars behind
 * a dashed target line, and the executive's trend lays actual and forecast lines
 * over plan bars. Splitting either into two charts would lose the comparison
 * that is the entire point of the card.
 *
 * Each series declares its own `type`. Bars sharing a `stack` name stack
 * together; a `dash` turns a line dotted, which is how every chart in the
 * prototype distinguishes a plan from an outcome.
 *
 * @param {{data:object[], xKey:string, height?:number, legend?:boolean,
 *          unit?:(n:number)=>string,
 *          series:{key:string,label:string,type?:'bar'|'line',color?:string,
 *                  dash?:boolean,stack?:string,fill?:boolean}[]}} props
 */
export function ComboChart({ data, xKey, series, height = 240, legend = true, unit = moneyShort }) {
  const chartData = useMemo(
    () => ({
      labels: data.map((d) => d[xKey]),
      datasets: series.map((s, i) => {
        const colour = s.color ?? SERIES[i % SERIES.length];
        const common = { label: s.label, data: data.map((d) => d[s.key]), order: s.type === 'line' ? 0 : 1 };
        return s.type === 'line'
          ? {
              ...common,
              type: 'line',
              borderColor: colour,
              backgroundColor: s.fill ? `${colour}24` : 'transparent',
              fill: Boolean(s.fill),
              borderDash: s.dash ? [5, 4] : undefined,
              borderWidth: 2,
              tension: 0.35,
              pointRadius: 0,
              pointHoverRadius: 4,
              /* A stacked value axis stacks *every* dataset, lines included, and
                 Chart.js groups them by `stack` — which defaults to the dataset
                 type, so all lines share one group. The executive chart's
                 forecast line was therefore drawn at actual + forecast, twice as
                 high as the revenue it was meant to sit beside. A stack of its
                 own leaves each line at its own value; the bars still stack. */
              stack: `line-${i}`,
            }
          : {
              ...common,
              type: 'bar',
              backgroundColor: colour,
              stack: s.stack ?? 'a',
              borderRadius: 5,
              borderSkipped: false,
              barPercentage: 0.6,
              categoryPercentage: 0.7,
            };
      }),
    }),
    [data, xKey, series],
  );

  /* Stacking is a property of the axis, not of a dataset, so it has to be on
     whenever two or more bars share a stack — otherwise they draw on top of one
     another and the tallest simply hides the rest. With a single bar series
     there is nothing to stack, and leaving it off keeps the axis honest. */
  const bars = series.filter((s) => (s.type ?? 'bar') === 'bar');
  const stacked = bars.length > 1;
  const options = useMemo(() => {
    const o = baseOptions({ stacked, legend });
    o.scales.y.ticks.callback = (v) => unit(v);
    o.plugins.tooltip.callbacks = {
      label: (ctx) => `${ctx.dataset.label ?? ''}  ${unit(ctx.parsed.y)}`.trim(),
    };
    return o;
  }, [stacked, legend, unit]);

  return (
    <div style={{ height }}>
      <Bar data={chartData} options={options} />
    </div>
  );
}

/**
 * A single-row inline bar — a distribution shown at table-cell size.
 * Used where a full chart would out-weigh the one number it carries.
 */
export function Sparkbar({ data, valueKey = 'value', height = 34, tone = SERIES[0] }) {
  const max = Math.max(...data.map((d) => d[valueKey]), 1);
  return (
    <div className="sparkbar" style={{ height }} aria-hidden="true">
      {data.map((d, i) => (
        <span key={i} style={{ height: `${(d[valueKey] / max) * 100}%`, background: tone }} />
      ))}
    </div>
  );
}
